import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../axiosInstance';
import { toast } from '../../../utils/toast';
import validatePublishing from '../../../components/Admin/Workflows/Wizard/validators/publishingValidation';
import {
  resolveWorkflowJson,
  buildPublishPayload,
} from '../../../components/Admin/Workflows/Wizard/utils/prepareData';
import { createWorkflowSchema } from '../../../validation/workflow';
import { createWorkflowDraftSchema } from '../../../validation/workflowDraft';
import { createWorkflowTemplateSchema } from '../../../validation/workflowTemplate';

const useWizardActions = ({
  values,
  setValues,
  setFieldTouched,
  setFieldError,
  resetForm,
  versionId,
  draftId,
  wfId,
  navigateBack,
  workflowBuilderRef,
  preJsonData,
  setPreJsonData,
  publishedAtValue,
  originalPublishedAt,
  originalName,
  setPublishedAtValue,
  setIsDraftSaved,
}) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const validateWithSchema = useCallback((schema, data) => {
    const result = schema.safeParse(data);
    if (result.success) return true;
    // join all messages
    const messages = result.error.issues
      .map((issue) => issue.message)
      .join('\n');
    toast.error(messages);
    return false;
  }, []);

  const prepareData = useCallback(() => {
    const workflowJson = resolveWorkflowJson(workflowBuilderRef, preJsonData);
    if (!workflowJson) {
      toast.error(t('workflowWizard.dataInvalidOrEmpty'));
      return null;
    }
    return buildPublishPayload(workflowJson, values, publishedAtValue);
  }, [workflowBuilderRef, preJsonData, values, publishedAtValue, t]);

  const handleWorkflowSave = useCallback(
    async ({ method, url, successMessage, dataPrepared }) => {
      if (!dataPrepared) {
        toast.error(t('workflowWizard.templateDataError'));
        return;
      }

      try {
        await axiosInstance[method](url, dataPrepared, {
          withCredentials: true,
        });
        toast.success(successMessage);
        setIsDraftSaved(true);
        navigateBack();
      } catch (err) {
        console.error('Error in save workflow:', err);
        toast.error(t('workflowWizard.saveError'));
      }
    },
    [navigateBack, t, setIsDraftSaved]
  );

  const submitSaveAsTemplate = useCallback(async () => {
    const dataPrepared = prepareData();
    if (!validateWithSchema(createWorkflowTemplateSchema(t), dataPrepared))
      return;
    await handleWorkflowSave({
      method: 'post',
      url: `${process.env.REACT_APP_SERVER_URL}/api/workflows-template`,
      successMessage: t('workflowWizard.savedAsTemplate'),
      dataPrepared,
    });
  }, [handleWorkflowSave, validateWithSchema, prepareData, t]);

  const saveAsDraft = useCallback(async () => {
    if (versionId) {
      setValues((prev) => ({
        ...prev,
        versionId: versionId,
      }));
    }

    const isUpdate = Boolean(draftId);
    const dataPrepared = prepareData();
    if (!validateWithSchema(createWorkflowDraftSchema(t), dataPrepared)) return;
    await handleWorkflowSave({
      method: isUpdate ? 'put' : 'post',
      url: isUpdate
        ? `${process.env.REACT_APP_SERVER_URL}/api/workflows-drafts/${draftId}`
        : `${process.env.REACT_APP_SERVER_URL}/api/workflows-drafts`,
      successMessage: isUpdate
        ? t('workflowWizard.draftUpdated')
        : t('workflowWizard.draftSaved'),
      dataPrepared,
    });
  }, [
    versionId,
    draftId,
    setValues,
    handleWorkflowSave,
    validateWithSchema,
    prepareData,
    t,
  ]);

  const handleBackStep = useCallback(() => {
    if (workflowBuilderRef.current) {
      const jsonData = workflowBuilderRef.current.getWorkflowJsonData();
      if (jsonData) {
        setPreJsonData(jsonData);
      }
    }
    setActiveStep((prev) => prev - 1);
  }, [workflowBuilderRef, setPreJsonData]);

  const handleNextStep = useCallback(
    async (validateFn, formValues) => {
      const errors = await validateFn(formValues);

      Object.keys(formValues).forEach((field) => {
        setFieldTouched(field, true, false);
        setFieldError(field, errors[field] || '');
      });

      if (Object.keys(errors).length === 0) {
        setActiveStep((prev) => prev + 1);
      }
    },
    [setFieldTouched, setFieldError]
  );

  const handleDiscardOpen = useCallback(() => {
    setShowDiscardModal(true);
  }, []);

  const handleDiscardClose = useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  const confirmDiscard = useCallback(() => {
    resetForm();

    if (workflowBuilderRef.current) {
      workflowBuilderRef.current.resetWorkflow();
      toast.success(t('workflowWizard.discardedSuccess'));

      setIsDraftSaved(true);
      navigateBack();
    }

    setShowDiscardModal(false);
  }, [resetForm, workflowBuilderRef, navigateBack, t, setIsDraftSaved]);

  const dataPublishingValidation = useCallback(async () => {
    if (workflowBuilderRef.current) {
      const jsonData = workflowBuilderRef.current.getWorkflowJsonData();

      const errors = await validatePublishing(jsonData);

      if (errors && Object.keys(errors).length > 0) {
        toast.warning(errors.message || t('workflowWizard.validationFailed'));
        return false;
      } else if (
        versionId &&
        originalPublishedAt &&
        new Date() >= originalPublishedAt
      ) {
        if (originalName === values.name) {
          toast.warning(t('workflowWizard.changeNameForNewVersion'));
          return false;
        }

        setPublishedAtValue(new Date());
      }
    } else {
      toast.warning(t('workflowWizard.dataNotAvailable'));
      return false;
    }

    toast.success(t('workflowWizard.validationPassed'));
    return true;
  }, [
    workflowBuilderRef,
    versionId,
    originalPublishedAt,
    originalName,
    values.name,
    setPublishedAtValue,
    t,
  ]);

  const handlePublishOpen = useCallback(async () => {
    const isValid = await dataPublishingValidation();
    if (isValid) {
      setShowPublishModal(true);
    }
  }, [dataPublishingValidation]);

  const handlePublishClose = useCallback(() => {
    setShowPublishModal(false);
  }, []);

  const confirmPublish = useCallback(async () => {
    if (!workflowBuilderRef.current) {
      toast.warning(t('workflowWizard.ensureCreated'));
      return;
    }

    const dataPrepared = prepareData();
    if (!dataPrepared) {
      toast.warning(t('workflowWizard.dataError'));
      return;
    }

    if (!validateWithSchema(createWorkflowSchema(t), dataPrepared)) return;

    try {
      let response;

      if (
        versionId &&
        originalPublishedAt &&
        new Date() < originalPublishedAt
      ) {
        response = await axiosInstance.put(
          `${process.env.REACT_APP_SERVER_URL}/api/workflows/${wfId}/version/${versionId}`,
          dataPrepared,
          { withCredentials: true }
        );
      } else if (!wfId) {
        response = await axiosInstance.post(
          `${process.env.REACT_APP_SERVER_URL}/api/workflows`,
          dataPrepared,
          { withCredentials: true }
        );
      } else {
        response = await axiosInstance.post(
          `${process.env.REACT_APP_SERVER_URL}/api/workflows/${wfId}/version`,
          dataPrepared,
          { withCredentials: true }
        );
      }

      if (response && draftId) {
        await axiosInstance.delete(
          `${process.env.REACT_APP_SERVER_URL}/api/workflows-drafts/${draftId}`,
          { withCredentials: true }
        );
      }

      setIsDraftSaved(true);
      navigateBack();

      toast.success(t('workflowWizard.publishedSuccess'));
    } catch (err) {
      console.error('Error in handleCreateAndUpdate:', err);

      if (err.response?.data?.errors) {
        Object.entries(err.response.data.errors).forEach(([key, value]) => {
          toast.error(
            t('workflowWizard.errorInField', { field: key, error: value })
          );
        });
      } else {
        toast.error(t('workflowWizard.publishError'));
      }
    }

    setShowPublishModal(false);
  }, [
    workflowBuilderRef,
    prepareData,
    validateWithSchema,
    versionId,
    originalPublishedAt,
    wfId,
    draftId,
    navigateBack,
    t,
    setIsDraftSaved,
  ]);

  return {
    activeStep,
    showDiscardModal,
    showPublishModal,
    prepareData,
    handleWorkflowSave,
    saveAsDraft,
    submitSaveAsTemplate,
    handleBackStep,
    handleNextStep,
    handleDiscardOpen,
    handleDiscardClose,
    confirmDiscard,
    dataPublishingValidation,
    handlePublishOpen,
    handlePublishClose,
    confirmPublish,
  };
};

export default useWizardActions;
